import { useEffect, useRef, useState } from 'react'
import EmpresaLayout from '@/components/EmpresaLayout'
import { useAuth } from '@/hooks/useAuth'
import { useInterviewTemplates, type InterviewTemplate } from '@/hooks/useInterviewTemplates'
import {
  FIELD_TYPE_LABELS,
  TEMPLATE_CATEGORIES,
  DEFAULT_COMPETENCIES,
  genFieldId,
  type FieldType,
  type TemplateField,
} from '@/lib/recruitment'
import {Plus, Trash2, Edit2, FileText, X, GripVertical, Power} from 'lucide-react'

const NEEDS_OPTIONS: FieldType[] = ['opcion_multiple', 'casillas', 'lista']

// ─── Drag-and-drop reorder hook ───────────────────────────────────────────────
function useDragReorder<T>(
  items: T[],
  setItems: React.Dispatch<React.SetStateAction<T[]>>,
  getId: (item: T) => string
) {
  const dragId = useRef<string | null>(null)

  const onDragStart = (id: string) => { dragId.current = id }

  const onDragOver = (e: React.DragEvent, overId: string) => {
    e.preventDefault()
    if (!dragId.current || dragId.current === overId) return
    setItems((prev) => {
      const from = prev.findIndex((x) => getId(x) === dragId.current)
      const to = prev.findIndex((x) => getId(x) === overId)
      if (from === -1 || to === -1) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  const onDragEnd = () => { dragId.current = null }

  return { onDragStart, onDragOver, onDragEnd }
}

// ─── Opciones inline para opcion_multiple / casillas / lista ─────────────────
function OptionsEditor({
  options,
  onChange,
}: {
  options: string[]
  onChange: (opts: string[]) => void
}) {
  const [draft, setDraft] = useState('')

  const add = () => {
    const v = draft.trim()
    if (!v || options.includes(v)) return
    onChange([...options, v])
    setDraft('')
  }

  const remove = (idx: number) => onChange(options.filter((_, i) => i !== idx))

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {options.map((o, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800"
          >
            {o}
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-amber-400 hover:text-red-500"
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="Nueva opción..."
          className="flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs focus:border-amber-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={add}
          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
        >
          + Añadir
        </button>
      </div>
    </div>
  )
}

// ─── Componente principal ─────────────────────────────────────────────────────
function EmpresaPlantillas() {
  const { user } = useAuth()
  const { templates, loading, fetchByCompany, createTemplate, updateTemplate, deleteTemplate } =
    useInterviewTemplates()

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<InterviewTemplate | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [category, setCategory] = useState(TEMPLATE_CATEGORIES[0])
  const [description, setDescription] = useState('')
  const [scaleType, setScaleType] = useState<'10' | '100'>('10')
  const [fields, setFields] = useState<TemplateField[]>([])
  const [competencies, setCompetencies] = useState<string[]>([...DEFAULT_COMPETENCIES])
  const [newCompetency, setNewCompetency] = useState('')

  const { onDragStart, onDragOver, onDragEnd } = useDragReorder(fields, setFields, (f) => f.id)

  useEffect(() => {
    if (user?.userId) fetchByCompany(user.userId)
  }, [user, fetchByCompany])

  const resetForm = () => {
    setName('')
    setCategory(TEMPLATE_CATEGORIES[0])
    setDescription('')
    setScaleType('10')
    setFields([])
    setCompetencies([...DEFAULT_COMPETENCIES])
    setNewCompetency('')
    setEditing(null)
  }

  const openCreate = () => { resetForm(); setShowForm(true) }

  const openEdit = (t: InterviewTemplate) => {
    setEditing(t)
    setName(t.name || '')
    setCategory(t.category || TEMPLATE_CATEGORIES[0])
    setDescription(t.description || '')
    setScaleType((t.scaleType as '10' | '100') || '10')
    setFields(t.fields ? [...t.fields] : [])
    setCompetencies(t.competencies ? [...t.competencies] : [...DEFAULT_COMPETENCIES])
    setShowForm(true)
  }

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { id: genFieldId(), label: '', type: 'texto_corto', required: false, options: [] },
    ])
  }

  const updateField = (id: string, patch: Partial<TemplateField>) => {
    setFields((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)))
  }

  const removeField = (id: string) => setFields((prev) => prev.filter((f) => f.id !== id))

  const addCompetency = () => {
    const v = newCompetency.trim()
    if (!v || competencies.includes(v)) return
    setCompetencies((prev) => [...prev, v])
    setNewCompetency('')
  }

  const handleSave = async () => {
    if (!user?.userId || !name.trim()) {
      alert('El nombre de la plantilla es obligatorio.')
      return
    }
    const payload = {
      companyUserId: user.userId,
      companyName: user.email || '',
      name: name.trim(),
      category,
      description: description.trim(),
      scaleType,
      fields: fields.map((f) => ({
        ...f,
        label: f.label.trim(),
        options: NEEDS_OPTIONS.includes(f.type)
          ? (f.options || []).filter((o) => o.trim())
          : undefined,
      })),
      competencies,
    }
    try {
      if (editing) {
        await updateTemplate(editing._id, payload)
      } else {
        await createTemplate(payload)
      }
      setShowForm(false)
      resetForm()
      fetchByCompany(user.userId)
    } catch (e) {
      console.error('Error al guardar plantilla:', e)
      alert('Ocurrió un error al guardar la plantilla.')
    }
  }

  const handleDelete = async (t: InterviewTemplate) => {
    if (!user?.userId) return
    if (!confirm(`¿Eliminar la plantilla "${t.name}"?`)) return
    try {
      await deleteTemplate(t._id)
      fetchByCompany(user.userId)
    } catch (e) {
      console.error('Error al eliminar plantilla:', e)
    }
  }

  const toggleActive = async (t: InterviewTemplate) => {
    if (!user?.userId) return
    try {
      await updateTemplate(t._id, { active: !t.active })
      fetchByCompany(user.userId)
    } catch (e) {
      console.error('Error al cambiar estado:', e)
    }
  }

  return (
    <EmpresaLayout
      title="Plantillas de entrevista"
      subtitle="Crea formularios personalizados para estandarizar tus procesos de selección"
    >
      <div className="mb-5 flex justify-end">
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus size={16} /> Nueva plantilla
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400">Cargando plantillas...</div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
          <FileText className="mx-auto mb-3 text-gray-300" size={40} />
          <p className="font-semibold text-gray-700">Aún no tienes plantillas</p>
          <p className="mt-1 text-sm text-gray-400">
            Crea tu primera plantilla para empezar a documentar entrevistas.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <div key={t._id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <span className="inline-block rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">
                    {t.category || 'General'}
                  </span>
                  <h3 className="mt-2 font-bold text-gray-900">{t.name}</h3>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    t.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {t.active ? 'Activa' : 'Inactiva'}
                </span>
              </div>
              {t.description ? (
                <p className="mt-2 line-clamp-2 text-sm text-gray-500">{t.description}</p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] text-gray-500">
                <span className="rounded bg-gray-100 px-2 py-0.5">{t.fields?.length || 0} campos</span>
                <span className="rounded bg-gray-100 px-2 py-0.5">
                  {t.competencies?.length || 0} competencias
                </span>
                <span className="rounded bg-gray-100 px-2 py-0.5">Escala 1-{t.scaleType || '10'}</span>
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
                <button
                  onClick={() => openEdit(t)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <Edit2 size={13} /> Editar
                </button>
                <button
                  onClick={() => toggleActive(t)}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <Power size={13} /> {t.active ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  onClick={() => handleDelete(t)}
                  className="ml-auto inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal constructor */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <div className="my-8 w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">
                {editing ? 'Editar plantilla' : 'Nueva plantilla de entrevista'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
              {/* Datos generales */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Nombre *</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ej. Entrevista Ventas"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Categoría</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  >
                    {TEMPLATE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Escala de calificación general
                </label>
                <div className="flex gap-2">
                  {(['10', '100'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setScaleType(s)}
                      className={`rounded-lg border px-4 py-2 text-sm font-medium ${
                        scaleType === s
                          ? 'border-amber-400 bg-amber-50 text-amber-700'
                          : 'border-gray-300 text-gray-600'
                      }`}
                    >
                      1 a {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Constructor de campos con drag-and-drop */}
              <div className="mt-6 border-t border-gray-100 pt-5">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Preguntas del formulario</h3>
                    {fields.length > 1 && (
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        Arrastra el ícono <GripVertical className="inline" size={12} /> para reordenar
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={addField}
                    className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                  >
                    <Plus size={13} /> Agregar pregunta
                  </button>
                </div>

                {fields.length === 0 ? (
                  <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-400">
                    Agrega preguntas personalizadas para esta plantilla.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {fields.map((f) => (
                      <div
                        key={f.id}
                        draggable
                        onDragStart={() => onDragStart(f.id)}
                        onDragOver={(e) => onDragOver(e, f.id)}
                        onDragEnd={onDragEnd}
                        className="rounded-lg border border-gray-200 bg-gray-50 p-3 transition-shadow hover:shadow-sm"
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical
                            className="mt-2 flex-shrink-0 cursor-grab text-gray-400 active:cursor-grabbing"
                            size={16}
                          />
                          <div className="flex-1 space-y-2">
                            <div className="flex flex-col gap-2 sm:flex-row">
                              <input
                                value={f.label}
                                onChange={(e) => updateField(f.id, { label: e.target.value })}
                                placeholder="Escribe la pregunta"
                                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                              />
                              <select
                                value={f.type}
                                onChange={(e) => {
                                  const newType = e.target.value as FieldType
                                  updateField(f.id, {
                                    type: newType,
                                    options: NEEDS_OPTIONS.includes(newType) ? (f.options || []) : [],
                                  })
                                }}
                                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                              >
                                {Object.entries(FIELD_TYPE_LABELS).map(([v, l]) => (
                                  <option key={v} value={v}>{l}</option>
                                ))}
                              </select>
                            </div>

                            {/* Opciones para opcion_multiple / casillas / lista */}
                            {NEEDS_OPTIONS.includes(f.type) && (
                              <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-3">
                                <p className="mb-2 text-[11px] font-semibold text-amber-700">
                                  Opciones de respuesta
                                </p>
                                <OptionsEditor
                                  options={f.options || []}
                                  onChange={(opts) => updateField(f.id, { options: opts })}
                                />
                                {(f.options || []).length === 0 && (
                                  <p className="mt-1.5 text-[10px] text-amber-600">
                                    Añade al menos una opción para esta pregunta.
                                  </p>
                                )}
                              </div>
                            )}

                            {f.type === 'escala' && (
                              <select
                                value={f.scaleMax || 5}
                                onChange={(e) =>
                                  updateField(f.id, { scaleMax: parseInt(e.target.value, 10) })
                                }
                                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                              >
                                <option value={5}>1 a 5</option>
                                <option value={10}>1 a 10</option>
                                <option value={100}>1 a 100</option>
                              </select>
                            )}

                            <label className="flex items-center gap-2 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={f.required}
                                onChange={(e) => updateField(f.id, { required: e.target.checked })}
                                className="rounded border-gray-300"
                              />
                              Campo obligatorio
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeField(f.id)}
                            className="mt-1 flex-shrink-0 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Competencias */}
              <div className="mt-6 border-t border-gray-100 pt-5">
                <h3 className="mb-3 font-semibold text-gray-900">Competencias a evaluar</h3>
                <div className="mb-3 flex flex-wrap gap-2">
                  {competencies.map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
                    >
                      {c}
                      <button
                        type="button"
                        onClick={() => setCompetencies((prev) => prev.filter((x) => x !== c))}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={newCompetency}
                    onChange={(e) => setNewCompetency(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCompetency())}
                    placeholder="Agregar competencia"
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={addCompetency}
                    className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                  >
                    Agregar
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="rounded-lg bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 hover:bg-amber-300"
              >
                {editing ? 'Guardar cambios' : 'Crear plantilla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </EmpresaLayout>
  )
}

export default EmpresaPlantillas
